"use client"

import {
    Cable,
    Compass,
    GraduationCap,
    Languages,
    type LucideIcon,
    SlidersHorizontal,
    UserCog,
    Users,
} from "lucide-react"

import { cn } from "@/common/lib/utils"
import ChartTheme from "@/modules/lernpfad/components/charts/chart-theme"
import DemoLevers from "@/modules/lernpfad/components/demo-levers"
import EmployeeView from "@/modules/lernpfad/components/employee-view"
import HrView from "@/modules/lernpfad/components/hr-view"
import IntegrationsView from "@/modules/lernpfad/components/integrations/integrations-view"
import Landing from "@/modules/lernpfad/components/landing/landing"
import LmsShell from "@/modules/lernpfad/components/lms/lms-shell"
import ManagerView from "@/modules/lernpfad/components/manager-view"
import SteuerungView from "@/modules/lernpfad/components/steuerung/steuerung-view"
import DemoTour from "@/modules/lernpfad/components/tour/demo-tour"
import { LANGS } from "@/modules/lernpfad/i18n/ui"
import { useLernpfadDemo, type Persona } from "./use-lernpfad-demo"
import { useMeasuredHeight } from "@/modules/lernpfad/components/use-measured-height"

const PERSONAS: Array<{ id: Persona; labelKey: string; icon: LucideIcon }> = [
    { id: "mitarbeitende", labelKey: "persona.employee", icon: GraduationCap },
    { id: "fuehrung", labelKey: "persona.manager", icon: UserCog },
    { id: "hr", labelKey: "persona.hr", icon: Users },
    { id: "integrationen", labelKey: "int.tab", icon: Cable },
    { id: "steuerung", labelKey: "persona.steuerung", icon: SlidersHorizontal },
]

const LANG_LABEL: Record<string, string> = { de: "DE", en: "EN" }

export default function LernpfadDemo() {
    const state = useLernpfadDemo()
    const { stage, setStage, persona, setPersona, lang, setLang, localizer } = state
    const { t } = localizer

    // The demo bar's height, published as a custom property so everything that
    // sticks BELOW it can offset itself correctly. It replaces a hardcoded
    // `top-[var(--lp-bar-height,72px)]` repeated in three files — which was already approximate and
    // would drift the moment a second sticky level or a wrapped bar changed it.
    // Same mechanism `packages/common/components/banner.tsx` uses for
    // `--banner-height`.
    const [barRef, scrollRef] = useMeasuredHeight("--lp-bar-height")

    return (
        <ChartTheme>
            <div
                ref={scrollRef}
                // Marked so in-page navigation can find the real scroll
                // container. An IntersectionObserver left on the default root
                // watches the viewport, which never scrolls here — it would
                // report every section as visible, forever, silently.
                data-lp-scroll
                className="h-full overflow-y-auto bg-muted/20"
            >
                {/* Sticky demo bar: the view switch and the language toggle, both
                    on top and both labelled as demo controls rather than as app
                    navigation — a viewer must never mistake the persona switch
                    for "who am I logged in as". */}
                <div
                    ref={barRef}
                    className="sticky top-0 z-20 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
                >
                    <div className="mx-auto flex w-full max-w-[1400px] flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3 xl:px-6">
                        <button
                            type="button"
                            onClick={() => setStage("landing")}
                            className="flex items-center gap-2"
                            title={t("app.title")}
                        >
                            <Compass className="size-5 text-primary" />
                            <span className="text-sm font-bold tracking-tight">Compass</span>
                            <span className="rounded-md bg-primary px-1.5 py-0.5 text-xs font-bold tracking-wide text-primary-foreground">
                                {t("app.badge")}
                            </span>
                        </button>

                        {/* View switch — a demo control, hidden on the landing stage */}
                        <div className={cn("flex items-center gap-2", stage === "landing" && "invisible")}>
                            <span className="hidden text-xs font-medium text-muted-foreground sm:inline">
                                {t("demo.viewSwitch")}
                            </span>
                            <div className="inline-flex rounded-lg border bg-card p-0.5">
                                {PERSONAS.map(({ id, labelKey, icon: Icon }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => setPersona(id)}
                                        aria-pressed={persona === id}
                                        className={cn(
                                            "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                                            persona === id
                                                ? "bg-primary text-primary-foreground shadow-sm"
                                                : "text-muted-foreground hover:bg-muted hover:text-foreground",
                                        )}
                                    >
                                        <Icon className="size-4" />
                                        {t(labelKey as never)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Language toggle */}
                        <div className="ml-auto flex items-center gap-2">
                            <Languages className="size-4 text-muted-foreground" />
                            <div className="inline-flex rounded-lg border bg-card p-0.5">
                                {LANGS.map((candidate) => (
                                    <button
                                        key={candidate}
                                        type="button"
                                        onClick={() => setLang(candidate)}
                                        aria-pressed={lang === candidate}
                                        aria-label={`${t("app.language")}: ${LANG_LABEL[candidate]}`}
                                        className={cn(
                                            "rounded-md px-2.5 py-1 text-xs font-semibold transition-colors",
                                            lang === candidate
                                                ? "bg-primary text-primary-foreground"
                                                : "text-muted-foreground hover:bg-muted hover:text-foreground",
                                        )}
                                    >
                                        {LANG_LABEL[candidate]}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {stage === "landing" ? (
                    <Landing state={state} />
                ) : (
                    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 xl:px-6">
                        <div
                            className={cn(
                                "grid gap-6",
                                persona !== "integrationen" &&
                            persona !== "steuerung" &&
                            "lg:grid-cols-[1fr_320px]",
                            )}
                        >
                            <main className="min-w-0">
                                {persona === "mitarbeitende" && <EmployeeView state={state} />}
                                {persona === "fuehrung" && <ManagerView state={state} />}
                                {persona === "hr" && <HrView state={state} />}
                                {persona === "integrationen" && <IntegrationsView state={state} />}
                                {persona === "steuerung" && <SteuerungView state={state} />}
                            </main>

                            {persona !== "integrationen" && persona !== "steuerung" && (
                                <aside className="lg:sticky lg:top-[var(--lp-bar-height,72px)] lg:self-start">
                                    <DemoLevers state={state} />
                                </aside>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* The simulated LMS. Mounted once here rather than inside whichever
                view opened it — both EmployeeView's "Hier starten" and
                IntegrationsView's preview tile reach it, and a Dialog portals
                out of the DOM tree anyway, so its position here is only about
                having one shared instance, not about layout. */}
            {state.lmsShell && <LmsShell state={state} shell={state.lmsShell} />}

            {/* The guided path. Outside the scroll container so it stays put
                while the view behind it scrolls, and below the LMS Dialog's
                z-50 so opening the portal covers it rather than fighting it. */}
            <DemoTour state={state} />
        </ChartTheme>
    )
}
